// src/store/slices/callSlice.ts

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Define a serializable version of MediaDeviceInfo
export interface SerializableDeviceInfo {
    deviceId: string;
    kind: MediaDeviceKind;
    label: string;
    groupId: string;
}

// Define Participant interface (ensure it matches your existing one)
export interface Participant {
    id: string;
    name: string;
    avatar: string;
    isMuted: boolean;
    isVideoOff: boolean;
    cameraStream: MediaStream | null;
    screenShareStream: MediaStream | null;
    isScreenSharing: boolean;
    status?: 'connecting' | 'connected' | 'disconnected' | 'failed' | 'connecting' | 'reconnecting';
    // streamVersion: number;
}

// Define ChatMessage interface
export interface ChatMessage {
    sender: string;
    message: string;
    timestamp: number;
    messageType?: 'chat' | 'whiteboard';
    typing?: boolean;
}

export interface FabricObjectData {
    type: string;
    version: string;
    originX: string;
    originY: string;
    left: number;
    top: number;
    width: number;
    height: number;
    fill: string | null;
    stroke: string | null;
    strokeWidth: number;
    text?: string;
    fontFamily?: string;
    fontSize?: number;
    objects?: FabricObjectData[];
    id?: string;
    [key: string]: any;
}

// Define a type for whiteboard drawing events
export interface WhiteboardDrawingEvent {
    id: string;
    type: 'whiteboard_object_added' | 'whiteboard_object_modified' | 'whiteboard_object_removed' | 'close_whiteboard' | 'whiteboard_image_added' | 'clear_whiteboard';
    data?: FabricObjectData | string;
    senderId: string;
    timestamp: number;
}

// Define the shape of the call state
export interface CallState {
    localStream: MediaStream | null;
    lobbyStream: Participant | null;
    peers: Participant[];
    connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'failed' | 'error' | 'reconnecting' | 'ActiveConnecting';
    isCallActive: boolean;
    userId: string | null;
    chatMessages: ChatMessage[];
    localMicOn: boolean;
    localVideoOn: boolean;
    screenShareActive: boolean;
    activeSpeakerInfo: Participant | null;
    activeSpeakerStream: MediaStream | null;
    selectedMic: string;
    selectedCamera: string;
    availableMics: SerializableDeviceInfo[];
    availableCameras: SerializableDeviceInfo[];
    isTokenAcquired: boolean;
    isWhiteboardOpen: boolean;
    whiteboardEvents: WhiteboardDrawingEvent[];
    callStartTime: number | null;
    // areStreamsReady will be a derived selector, not stored directly
}

// Initial state for the call slice
const initialState: CallState = {
    localStream: null,
    lobbyStream: null,
    peers: [],
    connectionStatus: 'disconnected',
    isCallActive: false,
    userId: null,
    chatMessages: [],
    localMicOn: true,
    localVideoOn: true,
    screenShareActive: false,
    activeSpeakerInfo: null,
    activeSpeakerStream: null,
    selectedMic: "",
    selectedCamera: "",
    availableMics: [],
    availableCameras: [],
    isTokenAcquired: false,
    isWhiteboardOpen: false,
    whiteboardEvents: [],
    callStartTime: null,
};

const callSlice = createSlice({
    name: 'call',
    initialState,
    reducers: {
        startCall: (state) => {
            state.isCallActive = true;
            state.callStartTime = Date.now();
        },
        endCall: (state) => {
            state.isCallActive = false;
            state.callStartTime = null;
        },
        setLocalStream: (state, action: PayloadAction<MediaStream | null>) => {
            // This action will now specifically manage the local camera stream.
            // Screen share will be handled separately.
            if (state.localStream && state.localStream.id !== action.payload?.id) {
                state.localStream.getTracks().forEach(track => track.stop());
            }

            state.localStream = action.payload;

            // Sync with the local user's peer object
            const localUser = state.peers.find(p => p.id === state.userId);
            if (localUser) {
                localUser.cameraStream = action.payload;
            }
        },
        addOrReplacePeer: (state, action: PayloadAction<Participant>) => {
            console.log("REDUX PEER UPDATE:", action.payload.id, {
                hasCamera: !!action.payload.cameraStream,
                hasScreen: !!action.payload.screenShareStream
            });

            const idx = state.peers.findIndex(p => p.id === action.payload.id);

            if (idx === -1) {
                state.peers.push(action.payload);
            } else {
                state.peers[idx] = action.payload;
            }
        },
        setLobbyStream: (state, action: PayloadAction<Participant | null>) => {
            if (state.lobbyStream && state.lobbyStream !== action.payload) {
                state.lobbyStream.cameraStream?.getTracks().forEach(track => track.stop()); // Stop old stream tracks
            }
            state.lobbyStream = action.payload;
        },
        addPeer: (state, action: PayloadAction<Participant>) => {
            const existingPeerIndex = state.peers.findIndex(p => p.id === action.payload.id);
            if (existingPeerIndex !== -1) {
                // Update existing peer, merging new properties.
                // This is crucial for updating cameraStream, screenShareStream, and isScreenSharing.
                state.peers[existingPeerIndex] = { ...state.peers[existingPeerIndex], ...action.payload };
            } else {
                // Add new peer
                state.peers.push(action.payload);
            }
        },
        removePeer: (state, action: PayloadAction<string>) => {
            const peerToRemove = state.peers.find(
                peer => peer.id === action.payload
            );

            // Stop tracks to avoid leaks / black frames
            if (peerToRemove) {
                // peerToRemove.cameraStream?.getTracks().forEach(t => t.stop());
                peerToRemove.screenShareStream?.getTracks().forEach(t => t.stop());
                peerToRemove.cameraStream = null;
                peerToRemove.screenShareStream = null;
            }

            // ✅ FIX: actually REMOVE the peer
            state.peers = state.peers.filter(
                peer => peer.id !== action.payload
            );

            // ✅ Reset active speaker if needed
            if (state.activeSpeakerInfo?.id === action.payload) {
                state.activeSpeakerInfo = null;
                state.activeSpeakerStream = null;
            }
        },
        setConnectionStatus: (state, action: PayloadAction<CallState['connectionStatus']>) => {
            state.connectionStatus = action.payload;
        },
        setIsCallActive: (state, action: PayloadAction<boolean>) => {
            state.isCallActive = action.payload;
        },
        setUserId: (state, action: PayloadAction<string | null>) => {
            state.userId = action.payload;
            // Removed the logic that pushes a new participant here.
            // The local participant will now be added/updated via `addPeer`
            // when RTCMultiConnection's onstream event fires for the local user.
        },
        addChatMessage: (state, action: PayloadAction<ChatMessage>) => {
            state.chatMessages.push(action.payload);
        },
        // NEW: Reducer to replace the entire chatMessages array
        setChatMessages: (state, action: PayloadAction<ChatMessage[]>) => {
            state.chatMessages = action.payload;
        },
        setLocalMicOn: (state, action: PayloadAction<boolean>) => {
            state.localMicOn = action.payload;
            const localUser = state.peers.find(p => p.id === state.userId);
            if (localUser) {
                localUser.isMuted = !action.payload;
            }
        },
        setLocalVideoOn: (state, action: PayloadAction<boolean>) => {
            state.localVideoOn = action.payload;
            const localUser = state.peers.find(p => p.id === state.userId);
            if (localUser) {
                localUser.isVideoOff = !action.payload;
            }
        },
        setScreenShareActive: (state, action: PayloadAction<boolean>) => {
            state.screenShareActive = action.payload;
            const localUser = state.peers.find(p => p.id === state.userId);
            if (localUser) {
                localUser.isScreenSharing = action.payload;
            }
        },
        setActiveSpeakerInfo: (state, action: PayloadAction<Participant | null>) => {
            state.activeSpeakerInfo = action.payload;
        },
        setActiveSpeakerStream: (state, action: PayloadAction<MediaStream | null>) => {
            if (state.activeSpeakerStream && state.activeSpeakerStream !== action.payload) {
                // Do not stop tracks here, as the stream might still be used by a peer.
                // The stream stopping should happen when the peer is removed or stream changes on the peer.
            }
            state.activeSpeakerStream = action.payload;
        },
        setSelectedMic: (state, action: PayloadAction<string>) => {
            state.selectedMic = action.payload;
        },
        setSelectedCamera: (state, action: PayloadAction<string>) => {
            state.selectedCamera = action.payload;
        },
        setAvailableMics: (state, action: PayloadAction<SerializableDeviceInfo[]>) => {
            state.availableMics = action.payload;
        },
        setAvailableCameras: (state, action: PayloadAction<SerializableDeviceInfo[]>) => {
            state.availableCameras = action.payload;
        },
        setIsTokenAcquired: (state, action: PayloadAction<boolean>) => {
            state.isTokenAcquired = action.payload;
        },
        resetCallState: (state) => {
            // Stop all active streams before resetting state
            if (state.localStream) {
                state.localStream.getTracks().forEach(track => track.stop());
            }
            // if (state.lobbyStream) {
            //     state.lobbyStream.getTracks().forEach(track => track.stop());
            // }
            state.peers.forEach(peer => {
                if (peer.cameraStream) {
                    peer.cameraStream.getTracks().forEach(track => track.stop());
                }
                if (peer.screenShareStream) {
                    peer.screenShareStream.getTracks().forEach(track => track.stop());
                }
            });
            // activeSpeakerStream is a reference, its tracks are stopped when the source peer/local stream is stopped.
            // if (state.activeSpeakerStream) {
            //     state.activeSpeakerStream.getTracks().forEach(track => track.stop());
            // }

            // Reset all state to initial values
            Object.assign(state, initialState);
            state.isWhiteboardOpen = false; // Ensure whiteboard is closed on reset
            state.whiteboardEvents = [];
        },
        setIsWhiteboardOpen: (state, action: PayloadAction<boolean>) => {
            state.isWhiteboardOpen = action.payload;
            // Optionally clear whiteboard events when it's closed
            if (!action.payload) {
                state.whiteboardEvents = [];
            }
        },
        addWhiteboardEvent: (state, action: PayloadAction<WhiteboardDrawingEvent>) => {
            state.whiteboardEvents.push(action.payload);
        },

    },
});

export const {
    setLocalStream,
    setLobbyStream,
    addPeer,
    addOrReplacePeer,
    removePeer,
    setConnectionStatus,
    setIsCallActive,
    setUserId,
    addChatMessage,
    setChatMessages,
    setLocalMicOn,
    setLocalVideoOn,
    setScreenShareActive,
    setActiveSpeakerInfo,
    setActiveSpeakerStream,
    setSelectedMic,
    setSelectedCamera,
    setAvailableMics,
    setAvailableCameras,
    setIsTokenAcquired,
    resetCallState,
    setIsWhiteboardOpen,
    addWhiteboardEvent,
    startCall,
    endCall
    // removeWhiteboardEvents,
    // clearAllWhiteboardEvents, 
} = callSlice.actions;

export default callSlice.reducer;
